/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthHeader from "@/components/AuthHeader";
import GoogleButton from "@/components/GoogleButton";
import { useAuth, formatAuthError } from "@/context/AuthContext";
import { auth } from "@/lib/firebase";

export default function Login() {
  const { login, resetPassword, resendVerification, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Giriş sonrası dönülecek yer (?next= veya state.from). Booking akışından gelince
  // anasayfaya değil kaldığı ekrana döner.
  const nextUrl = params.get("next") || location.state?.from || "";
  const kayitHref = nextUrl ? `/kayit?next=${encodeURIComponent(nextUrl)}` : "/kayit";
  const [email, setEmail] = useState("");
  const [sifre, setSifre] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const r = await signInWithGoogle();
      if (r.status === "existing") {
        const u = r.user;
        toast.success(`Hoş geldin, ${(u.ad_soyad || "").split(" ")[0] || "PAK üyesi"}!`);
        const dest = u.role === "admin" ? "/admin" : u.role === "saglayici" ? "/panel" : "/";
        navigate(nextUrl || dest, { replace: true });
      } else {
        toast("Neredeyse tamam — birkaç bilgi daha alalım.");
        navigate(`/kayit?social=1${nextUrl ? `&next=${encodeURIComponent(nextUrl)}` : ""}`, { replace: true });
      }
    } catch (err) {
      if (["auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(err?.code)) return;
      toast.error(formatAuthError(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !sifre) return toast.error("E-posta ve şifrenizi girin.");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), sifre);
      toast.success(`Hoş geldin, ${(u.ad_soyad || "").split(" ")[0] || "PAK üyesi"}!`);
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        toast.warning("E-postanı henüz doğrulamadın.", {
          description: "Bazı işlemler için e-posta doğrulaması gerekir.",
          action: {
            label: "Tekrar gönder",
            onClick: () =>
              resendVerification()
                .then(() => toast.success("Doğrulama e-postası gönderildi."))
                .catch(() => toast.error("Gönderilemedi, lütfen tekrar deneyin.")),
          },
          duration: 8000,
        });
      }
      const defaultRedirect = u.role === "admin" ? "/admin" : u.role === "saglayici" ? "/panel" : "/";
      const redirect = nextUrl || defaultRedirect;
      navigate(redirect, { replace: true });
    } catch (e) {
      toast.error(formatAuthError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onForgot = async () => {
    if (!email) return toast.error("Önce e-posta adresinizi girin.");
    try {
      await resetPassword(email.trim().toLowerCase());
      toast.success("Şifre sıfırlama bağlantısı e-postanıza gönderildi.");
    } catch (err) {
      toast.error(formatAuthError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <AuthHeader />

      <main className="flex-1 grid md:grid-cols-2">
        <div className="hidden md:flex relative bg-gradient-to-br from-emerald-50 via-white to-emerald-100/40 items-center justify-center p-12 overflow-hidden">
          <div className="absolute inset-0 pak-grid-bg opacity-40" />
          <div className="absolute -top-20 -left-20 h-80 w-80 rounded-full bg-[#10B981]/15 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl" />
          <div className="relative max-w-md">
            <div className="inline-flex h-14 w-14 rounded-2xl bg-[#10B981] text-white items-center justify-center mb-6">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-[#0F172A] leading-tight">
              PAK ailesine <br />tekrar hoş geldin.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Taleplerini yönet, gelen teklifleri karşılaştır ve uzmanlarla iletişime geç.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#0F172A]">
              <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Doğrulanmış uzmanlardan ücretsiz teklif</li>
              <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Teklifleri karşılaştır, doğrudan anlaş</li>
              <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> 7/24 müşteri desteği</li>
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">Giriş Yap</h1>
            <p className="mt-2 text-slate-600">
              Hesabın yok mu?{" "}
              <Link to={kayitHref} className="text-[#10B981] font-semibold hover:underline" data-testid="link-register">
                Kayıt Ol
              </Link>
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div>
                <Label className="mb-2 block">E-posta</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    className="h-12 rounded-xl pl-11"
                    data-testid="login-email"
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Şifre</Label>
                  <button
                    type="button"
                    onClick={onForgot}
                    className="text-xs text-[#10B981] hover:underline"
                    data-testid="link-forgot"
                  >
                    Şifremi unuttum
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={sifre}
                    onChange={(e) => setSifre(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 rounded-xl pl-11 pr-11"
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
                    aria-label="Şifreyi göster"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl bg-[#10B981] hover:bg-[#0EA371] text-white font-semibold"
                data-testid="login-submit"
              >
                {submitting ? "Giriş yapılıyor..." : <>Giriş Yap <ChevronRight className="ml-1 h-4 w-4" /></>}
              </Button>

              {/* Google girişi native uygulamada gizli — popup capacitor:// şemasında çalışmaz */}
              <div className="native-hide flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">veya</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="native-hide">
                <GoogleButton onClick={handleGoogle} loading={googleLoading} testid="login-google" />
              </div>

              <div className="text-center text-sm text-slate-500 pt-2">
                Hesabını oluşturarak{" "}
                <a href="#" className="text-[#10B981] hover:underline">KVKK</a> ve{" "}
                <a href="#" className="text-[#10B981] hover:underline">Kullanım Şartları</a>'nı kabul edersin.
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
